<template>
  <div class="SiteApp">
    <div class="image-wrapper">
      <AppThumbnail
        :image-url="app.forge_app.thumbnail_url || app.forge_app.icon_url || defaultIcon"
        :alt="app.forge_app.name"
        :fallback-text="app.forge_app.name"
        :show-fallback-text="false"
        class="app-thumbnail"
      />
      <div class="forge-native-tag">
        <the-icon icon="logo-small" width="16" height="16" class="mr-1" />
        Forge app
      </div>
      <div v-if="app.enabled" class="enabled-tag">Active</div>
      <div v-else class="disabled-tag">Inactive</div>
    </div>
    <div class="pa-5">
      <div class="d-flex justify-space-between items-center">
        <div class="app-name">
          {{ app.forge_app.name }}
        </div>
        <v-menu offset-x>
          <template v-slot:activator="{ on, attrs }">
            <v-btn text icon compact v-on="on">
              <the-icon icon="more-vert" />
            </v-btn>
          </template>
          <v-list dark class="the-app-bar__dropdown">
            <v-list-item v-if="app.forge_app.documentation_url">
              <v-list-item-title class="SiteApp__action" @click="openDocs">
                <span class="SiteApp__icon">
                  <v-icon>exit_to_app</v-icon>
                </span>
                Documentation
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title class="SiteApp__action" @click="configure">
                <span class="SiteApp__icon">
                  <v-icon>settings</v-icon>
                </span>
                {{ isUserFeedbackApp ? 'Manage feedback' : 'Configure' }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title class="SiteApp__action" @click="getSupport">
                <span class="SiteApp__icon">
                  <v-icon>help</v-icon>
                </span>
                Get Support
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <div @click="uninstall" class="SiteApp__action dangerous">
                <span class="SiteApp__icon">
                  <the-icon icon="trash" />
                </span>
                <span>Uninstall App</span>
              </div>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="mt-3 app-tag-wrapper">
        <span class="tag" v-for="categoryName in categoryNames" :key="categoryName">
          {{ categoryName }}
        </span>
      </div>
      <div class="app-description my-5">{{ app.forge_app.description }}</div>
      <div class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <the-icon
            class="mr-2"
            color="y1"
            icon="download"
            width="16"
            height="16"
          />
          {{ app.forge_app?.installed_count || 0 }}
        </div>
        <v-btn color="y1" text @click="configure">
          <the-icon class="mr-2" color="y1" icon="square-edit" />
          {{ isUserFeedbackApp ? 'Manage feedback' : 'Edit Settings' }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { MODAL_ALERT } from '@/components/Modals'
import {
  FORGE_APP_DEFAULT_THUMBNAIL,
  isUserFeedbackForgeApp,
} from '@/constants/forgeApps'
import AppThumbnail from './AppThumbnail.vue'

export default {
  name: 'SiteAppsForgeAppInstalled',
  components: {
    AppThumbnail
  },
  props: {
    app: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState({
      activeSite: (state) => state.sites.active,
    }),
    categoryNames() {
      return this.app?.forge_app?.category ? [this.app.forge_app.category.charAt(0).toUpperCase() + this.app.forge_app.category.slice(1)] : []
    },
    defaultIcon() {
      return FORGE_APP_DEFAULT_THUMBNAIL
    },
    isUserFeedbackApp() {
      return isUserFeedbackForgeApp(this.app)
    },
  },
  methods: {
    openDocs() {
      if (this.app?.forge_app?.documentation_url) {
        window.open(this.app.forge_app.documentation_url, '_blank')
      }
    },
    configure() {
      if (isUserFeedbackForgeApp(this.app)) {
        this.$router.push({ name: 'site_apps_user_feedback' })
        return
      }
      this.$emit('configure-app', this.app)
    },
    uninstall() {
      if (!this.app?.id || !this.activeSite?.id) {
        this.$root.$emit('showSnack', {
          message: 'Unable to uninstall app - missing data',
          color: 'error'
        })
        return
      }
      
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Uninstall app',
          text: `Are you sure you want to uninstall ${this.app.forge_app?.name || 'this app'}?`,
          actionBtnLabel: 'Uninstall',
          showCancelBtn: true,
          action: async () => {
            try {
              await this.$store.dispatch('forgeApps/uninstallApp', {
                siteId: this.activeSite.id,
                appSettingId: this.app.id
              })
              this.$emit('app-changed')
              this.$root.$emit('showSnack', {
                message: `${this.app.forge_app?.name || 'App'} uninstalled successfully`,
                color: 'success'
              })
            } catch (error) {
              this.$root.$emit('showSnack', {
                message: `Failed to uninstall ${this.app.forge_app?.name || 'app'}`,
                color: 'error'
              })
            }
          },
        },
      })
    },
    getSupport() {
      window.open(
        'https://community.beach.io/t/forge-apps-wishlist-feedback/36',
        '_blank'
      )
    }
  },
}
</script>

<style scoped lang="scss">
$style: SiteApp;
.#{$style} {
  border: 1px solid $N6;
  border-radius: 8px;
  background: $N7;
  .app-name {
    font-size: 16px;
  }
}
.#{$style}__icon {
  width: 30px;
  text-align: center;
  .v-icon.v-icon {
    font-size: 20px;
  }
}
.#{$style}__action {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  &.dangerous {
    color: $Y1;
  }
}
.image-wrapper {
  position: relative;
  margin-bottom: 5px;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}
.forge-native-tag {
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 6px;
  padding: 4px 6px;
  background: $B5;
  display: flex;
  align-items: center;
  z-index: 1;
}
.enabled-tag {
  position: absolute;
  top: 16px;
  left: 16px;
  border-radius: 6px;
  padding: 4px 12px;
  background: #28a745;
  color: white;
  font-size: 12px;
  font-weight: 500;
  z-index: 1;
}
.disabled-tag {
  position: absolute;
  top: 16px;
  left: 16px;
  border-radius: 6px;
  padding: 4px 12px;
  background: #6c757d;
  color: white;
  font-size: 12px;
  font-weight: 500;
  z-index: 1;
}
.app-description {
  min-height: 60px;
  white-space: pre-line;
}
.app-tag-wrapper {
  min-height: 32px;
  margin-bottom: 5px;
}
span.tag {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 400;
  border-radius: 6px;
  margin-right: 8px;
  background: $N5;
  margin-bottom: 5px;
  display: inline-block;
}
.app_type {
  display: inline-block;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 400;
  border-radius: 6px;
  background: $N5;
  margin-bottom: 5px;
}
</style> 